import type { InjectedRouter } from 'js/lib/connectToRouter';
import useRouter from 'js/lib/useRouter';
import type UserAgentInfo from 'js/lib/useragent';

import type { PropsFromCourseraPlusProductOwnerships } from 'bundles/coursera-plus-common/utils/withCourseraPlusProductOwnerships';
import { useCourseraPlusProductOwnerships } from 'bundles/coursera-plus-common/utils/withCourseraPlusProductOwnerships';
import { useEnrollment } from 'bundles/enroll-common/components/xdp/withEnrollment';
import type { PropsFromWithEnrollment } from 'bundles/enroll-common/components/xdp/withEnrollment';
import { useEnrollButtonQueries } from 'bundles/enroll-common/hooks/useEnrollButtonQueries';
import type { UseEnrollButtonQueriesResult } from 'bundles/enroll-common/hooks/useEnrollButtonQueries';
import useUserAgent from 'bundles/page/hooks/useUserAgent';

export type UseEnrollButtonDataProps = {
  courseId?: string;
  s12nId?: string;
  isSpecialization: boolean;
  partnerIds?: string[];
};

export type UseEnrollButtonDataOptions = {
  withEnrollmentNotRequired?: boolean;
};

// Everything this hook gathers by calling hooks directly.
export type UseEnrollButtonDataResult = Pick<UseEnrollButtonQueriesResult, 'course' | 's12ns' | 's12n'> &
  PropsFromWithEnrollment &
  PropsFromCourseraPlusProductOwnerships & {
    userAgent?: UserAgentInfo;
    router: InjectedRouter;
  };

/**
 * Single hook that gathers all the data the XDP enroll button needs by calling hooks directly:
 *   - `useUserAgent()`
 *   - `useRouter()`
 *   - `useEnrollButtonQueries()`
 *   - `useEnrollment()`
 *   - `useCourseraPlusProductOwnerships()`
 *
 * Called from the wrapper in `BannerEnroll.tsx`; its results are spread as props so the remaining
 * HOC layers that have no hook equivalent yet (`withPrice`, `withPromotionInfo`, the finaid
 * enhancers, and `withCourseraPlusProductEligibilityCheck`) can consume them, after which
 * `useEnrollButtonDerivedProps` turns the combined props into the final presentational props.
 *
 * `useEnrollment` and `useCourseraPlusProductOwnerships` both skip their queries internally when
 * the user is unauthenticated, so calling them unconditionally here preserves the behavior of the
 * old `branch(isAuthenticated)` gate for those two layers.
 */
export const useEnrollButtonData = (
  { courseId, s12nId, isSpecialization, partnerIds }: UseEnrollButtonDataProps,
  { withEnrollmentNotRequired }: UseEnrollButtonDataOptions = {}
): UseEnrollButtonDataResult => {
  const { course, s12ns, s12n } = useEnrollButtonQueries({ courseId, s12nId, isSpecialization });

  const userAgent = useUserAgent();
  const router = useRouter();

  const { enrollmentAvailableChoices, isLoadingEnrollmentChoices } = useEnrollment({
    isSpecialization,
    s12nId,
    courseId,
    partnerIds,
    isNotRequired: withEnrollmentNotRequired,
  });

  const {
    courseraPlusProductOwnership,
    courseraLiteProductOwnership,
    ownsCourseraPlus,
    ownsCourseraLite,
    hasUpgradedToCourseraPlus,
  } = useCourseraPlusProductOwnerships();

  return {
    course,
    s12ns,
    s12n,
    userAgent,
    router,
    enrollmentAvailableChoices,
    isLoadingEnrollmentChoices,
    courseraPlusProductOwnership,
    courseraLiteProductOwnership,
    ownsCourseraPlus,
    ownsCourseraLite,
    hasUpgradedToCourseraPlus,
  };
};

export default useEnrollButtonData;
